import React, { useState, useEffect } from "react";
import SearchFieldRow from "./SearchFieldRow";
import DateSearchRow from "./DateSearchRow";

/**
 * システム装置部門用 詳細検索フォームコンポーネント
 * - 全項目の状態管理
 * - 入力制限
 * - 親コンポーネントへの条件送信（onFormChange）
 */
const SouchiForm = ({ onFormChange, criteria, document_category = [], document_type = [] }) => {

    /**
     * 初期値（リセット時にも使用）
     * すべての入力フィールドと検索条件を含む
     */
    const defaultFormData = {
        // --- テキスト項目 ---
        auto_number: "",
        product_no: "",
        destination: "",
        customer: "",
        device_name: "",
        assembly_type: "",
        assignee: "",
        remarks: "",
        title: "",

        // --- 条件（0 = を含む） ---
        auto_number_condition: "0",
        product_no_condition: "0",
        destination_condition: "0",
        customer_condition: "0",
        device_name_condition: "0",
        assembly_type_condition: "0",
        assignee_condition: "0",
        remarks_condition: "0",
        title_condition: "0",

        // --- あいまい検索 ---
        auto_number_fuzzy: true,
        product_no_fuzzy: true,
        destination_fuzzy: true,
        customer_fuzzy: true,
        device_name_fuzzy: true,
        assembly_type_fuzzy: true,
        assignee_fuzzy: true,
        remarks_fuzzy: true,
        title_fuzzy: true,

        // --- セレクト項目 ---
        document_category: "",
    };

    // 入力値を管理する state
    const [formData, setFormData] = useState(defaultFormData);

    /**
     * 初回レンダリング：formData 全項目を親へ送信
     */
    useEffect(() => {
        if (onFormChange) {
            Object.entries(formData).forEach(([key, value]) => {
                onFormChange(key, value);
            });
        }
    // eslint-disable-next-line 
    }, []);

    /**
     * 親 criteria 更新監視
     * - 初期状態（キー <= 2）→ フォーム全リセット
     * - それ以外 → criteria を formData にマージ
     */
    useEffect(() => {

        if (criteria && Object.keys(criteria).length <= 2) {
            setFormData(defaultFormData);

            Object.entries(defaultFormData).forEach(([k, v]) => {
                onFormChange(k, v);
            });
            return;
        }

        if (criteria && Object.keys(criteria).length > 2) {
            setFormData((prev) => ({ ...prev, ...criteria }));
        }
    // eslint-disable-next-line 
    }, [criteria]);

    // 全角＝1文字、半角＝0.5文字でカウント
    const trimJapaneseByLength = (str, limit) => {
        let result = "";
        let length = 0;

        for (const ch of str) {
            const add = ch.match(/[!-~]/) ? 0.5 : 1;
            if (length + add > limit) break;
            result += ch;
            length += add;
        }
        return result;
    };

    // 全角英数字 → 半角英数字
    const toHankaku = (str) =>
        str.replace(/[！-～]/g, (ch) =>
            String.fromCharCode(ch.charCodeAt(0) - 0xFEE0)
    );

    /**
     * 入力イベントの共通処理
     * - 入力値の制限
     * - 半角変換
     * - 親への onFormChange 通知
     */
    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        switch (name) {
            case "auto_number":
                // 半角英数字＋ハイフンのみ、最大8文字
                newValue = toHankaku(value)
                    .replace(/[^0-9]/g, "")
                    .slice(0, 10);
                break;

            case "product_no":
                newValue = trimJapaneseByLength(value, 5);
                break;

            case "destination":
            case "customer":
            case "device_name":
            case "assembly_type":
            case "remarks":
                newValue = trimJapaneseByLength(value, 20);
                break;

            case "assignee":
                newValue = trimJapaneseByLength(value, 10);
                break;

            case "title":
                newValue = trimJapaneseByLength(value, 64);
                break;

            default:
                break;
        }

        const updated = { ...formData, [name]: newValue };
        setFormData(updated);
        onFormChange && onFormChange(name, newValue);
    };

    return (
        <div className="detail-grid-2col">

            {/* ───────── LEFT COLUMN ───────── */}
            <div className="form-column">

                {/* オートナンバー */}
                <div className="form-row">
                    <SearchFieldRow
                        label="オートナンバー"
                        name="auto_number"
                        value={formData.auto_number}
                        onChange={handleChange}
                        condition={formData.auto_number_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, auto_number_condition: val }));
                            onFormChange("auto_number_condition", val);
                        }}
                        isFuzzy={formData.auto_number_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, auto_number_fuzzy: checked }));
                            onFormChange("auto_number_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 製番 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="製番"
                        name="product_no"
                        value={formData.product_no}
                        onChange={handleChange}
                        condition={formData.product_no_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, product_no_condition: val }));
                            onFormChange("product_no_condition", val);
                        }}
                        isFuzzy={formData.product_no_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, product_no_fuzzy: checked }));
                            onFormChange("product_no_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 仕向先 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="仕向先"
                        name="destination"
                        value={formData.destination}
                        onChange={handleChange}
                        condition={formData.destination_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, destination_condition: val }));
                            onFormChange("destination_condition", val);
                        }}
                        isFuzzy={formData.destination_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, destination_fuzzy: checked }));
                            onFormChange("destination_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 得意先 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="得意先"
                        name="customer"
                        value={formData.customer}
                        onChange={handleChange}
                        condition={formData.customer_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, customer_condition: val }));
                            onFormChange("customer_condition", val);
                        }}
                        isFuzzy={formData.customer_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, customer_fuzzy: checked }));
                            onFormChange("customer_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 設備名 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="設備名"
                        name="device_name"
                        value={formData.device_name}
                        onChange={handleChange}
                        condition={formData.device_name_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, device_name_condition: val }));
                            onFormChange("device_name_condition", val);
                        }}
                        isFuzzy={formData.device_name_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, device_name_fuzzy: checked }));
                            onFormChange("device_name_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 組立種類 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="組立種類"
                        name="assembly_type"
                        value={formData.assembly_type}
                        onChange={handleChange}
                        condition={formData.assembly_type_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, assembly_type_condition: val }));
                            onFormChange("assembly_type_condition", val);
                        }}
                        isFuzzy={formData.assembly_type_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, assembly_type_fuzzy: checked }));
                            onFormChange("assembly_type_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 図面種類 */}
                <div className="form-row">
                    <div className="search-field">
                        <label>図面種類（システム装置）</label>
                        <select
                            name="document_category"
                            value={formData.document_category || ""}
                            onChange={handleChange}
                            style={{ flex: '1', width: '100%', border: '1px solid #fff' }}
                        >
                            <option value=""></option>
                            {document_category?.map((item) => (
                                <option key={item.display_idx} value={item.document_category}>
                                    {item.document_category_text}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>



            {/* ───────── RIGHT COLUMN ───────── */}
            <div className="form-column">

                {/* 日付検索 */}
                <div className="form-row">
                    <DateSearchRow
                        onChange={(value) => {
                            onFormChange("dateCondition", value);
                        }}
                    />
                </div>

                {/* 担当者 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="担当者"
                        name="assignee"
                        value={formData.assignee}
                        onChange={handleChange}
                        condition={formData.assignee_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, assignee_condition: val }));
                            onFormChange("assignee_condition", val);
                        }}
                        isFuzzy={formData.assignee_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, assignee_fuzzy: checked }));
                            onFormChange("assignee_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 備考 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="備考"
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        condition={formData.remarks_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, remarks_condition: val }));
                            onFormChange("remarks_condition", val);
                        }}
                        isFuzzy={formData.remarks_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, remarks_fuzzy: checked }));
                            onFormChange("remarks_fuzzy", checked);
                        }}
                    />
                </div>

                {/* タイトル */}
                <div className="form-row">
                    <SearchFieldRow
                        label="タイトル"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        condition={formData.title_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, title_condition: val }));
                            onFormChange("title_condition", val);
                        }}
                        isFuzzy={formData.title_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, title_fuzzy: checked }));
                            onFormChange("title_fuzzy", checked);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default SouchiForm;
